import { useState } from 'react';

import type { GetMarketplacePropertySearchQueryVariables } from '__generated__/athena/graphql';

import { buildPropertyTypes } from '../search/utils';
import type { PropertySearchQuery } from './useSearchQueries';

type SearchLocation = Pick<PropertySearchQuery, 'market' | 'zoneIds' | 'vertices'>;

/**
 * Builds a search payload directly from a canonical query rather than from the
 * hook's live state. `useSearchState`'s state setters are async, so callers
 * that want to fetch with values they're applying in the same tick (e.g.
 * switching to a saved search) must build the payload from those values
 * explicitly instead of relying on `buildSearchPayload`.
 */
export const buildPayloadFromQuery = (
  query: PropertySearchQuery,
  location: SearchLocation,
): GetMarketplacePropertySearchQueryVariables => {
  const propertyTypes = buildPropertyTypes(query.singleFamily, query.condo, query.townhome);

  // `noHoas` is a UI boolean; the search API only accepts `maxHoaMonthlyFee`,
  // so "no HOAs" maps to a $0 ceiling and an unset filter maps to null.
  // Lot-size (`minLotSize`/`maxLotSize`) has no GraphQL search input yet, so it
  // is intentionally not forwarded here — it stays in canonical state for the
  // URL and saved searches until the backend supports it.
  const payload: GetMarketplacePropertySearchQueryVariables = {
    bathrooms: query.bathrooms ?? 0,
    bedrooms: query.bedrooms ?? 0,
    canSelfTour: query.canSelfTour ?? null,
    hasPool: query.hasPool ?? false,
    maxHoaMonthlyFee: query.noHoas ? 0 : null,
    maxListPrice: query.maxListPrice ?? null,
    maxSquareFootage: query.maxSquareFootage ?? null,
    maxYearBuilt: query.maxYearBuilt ?? null,
    minListPrice: query.minListPrice ?? null,
    minSquareFootage: query.minSquareFootage ?? null,
    minYearBuilt: query.minYearBuilt ?? null,
    propertyTypes,
    singleStoryOnly: query.hasSingleStory ?? false,
  };

  // Location is mutually exclusive: vertices > zoneIds > market.
  const { vertices, zoneIds, market } = location;
  if (vertices?.length) {
    return { ...payload, vertices };
  }
  if (zoneIds?.length) {
    return { ...payload, zoneIds };
  }
  if (market) {
    return { ...payload, market };
  }
  return payload;
};

export const getInitialPropertyTypes = (queryParams: PropertySearchQuery) => {
  const hasAnyPropertyTypeParam =
    queryParams?.singleFamily !== undefined ||
    queryParams?.condo !== undefined ||
    queryParams?.townhome !== undefined;

  if (hasAnyPropertyTypeParam) {
    return {
      singleFamily: queryParams?.singleFamily ?? false,
      condo: queryParams?.condo ?? false,
      townhome: queryParams?.townhome ?? false,
    };
  }

  return { singleFamily: true, condo: true, townhome: true };
};

export const useSearchState = (queryParams: PropertySearchQuery) => {
  const initialPropertyTypes = getInitialPropertyTypes(queryParams);
  const [minBeds, setBedrooms] = useState(queryParams?.bedrooms || 0);
  const [minBaths, setBathrooms] = useState(queryParams?.bathrooms || 0);
  const [canSelfTour, setCanSelfTour] = useState<boolean | null>(queryParams?.canSelfTour ?? null);
  const [minPrice, setMinPrice] = useState(queryParams?.minListPrice || null);
  const [maxPrice, setMaxPrice] = useState(queryParams?.maxListPrice || null);
  const [singleFamily, setSingleFamily] = useState(initialPropertyTypes.singleFamily);
  const [condo, setCondo] = useState(initialPropertyTypes.condo);
  const [townhome, setTownhome] = useState(initialPropertyTypes.townhome);
  const [noHoas, setNoHoas] = useState(queryParams?.noHoas || false);
  const [singleStory, setSingleStory] = useState(queryParams?.hasSingleStory || false);
  const [mustHavePool, setMustHavePool] = useState(queryParams?.hasPool || false);
  const [minYearBuilt, setMinYearBuilt] = useState(queryParams?.minYearBuilt || null);
  const [maxYearBuilt, setMaxYearBuilt] = useState(queryParams?.maxYearBuilt || null);
  const [minLotSize, setMinLotSize] = useState(queryParams?.minLotSize || null);
  const [maxLotSize, setMaxLotSize] = useState(queryParams?.maxLotSize || null);
  const [minSquareFootage, setMinSquareFootage] = useState(queryParams?.minSquareFootage || null);
  const [maxSquareFootage, setMaxSquareFootage] = useState(queryParams?.maxSquareFootage || null);
  const [pageNumber, setPageNumber] = useState(0);

  const resetPage = () => setPageNumber(0);

  // Replaces every filter value at once from a canonical query (e.g. when a
  // saved search is applied). Property types follow the same all-or-nothing
  // convention as initial hydration.
  const applyCanonical = (query: PropertySearchQuery) => {
    const propertyTypes = getInitialPropertyTypes(query);
    setBedrooms(query.bedrooms || 0);
    setBathrooms(query.bathrooms || 0);
    setCanSelfTour(query.canSelfTour ?? null);
    setMinPrice(query.minListPrice || null);
    setMaxPrice(query.maxListPrice || null);
    setSingleFamily(propertyTypes.singleFamily);
    setCondo(propertyTypes.condo);
    setTownhome(propertyTypes.townhome);
    setNoHoas(query.noHoas || false);
    setSingleStory(query.hasSingleStory || false);
    setMustHavePool(query.hasPool || false);
    setMinYearBuilt(query.minYearBuilt || null);
    setMaxYearBuilt(query.maxYearBuilt || null);
    setMinLotSize(query.minLotSize || null);
    setMaxLotSize(query.maxLotSize || null);
    setMinSquareFootage(query.minSquareFootage || null);
    setMaxSquareFootage(query.maxSquareFootage || null);
    setPageNumber(0);
  };

  const resetFilters = () => {
    setBathrooms(0);
    setBedrooms(0);
    setCanSelfTour(null);
    setCondo(true);
    setMaxLotSize(null);
    setMaxPrice(null);
    setMaxSquareFootage(null);
    setMaxYearBuilt(null);
    setMinLotSize(null);
    setMinPrice(null);
    setMinSquareFootage(null);
    setMinYearBuilt(null);
    setMustHavePool(false);
    setNoHoas(false);
    setPageNumber(0);
    setSingleFamily(true);
    setSingleStory(false);
    setTownhome(true);
  };

  const getCanonical = (): PropertySearchQuery => ({
    bedrooms: minBeds,
    bathrooms: minBaths,
    canSelfTour,
    minListPrice: minPrice,
    maxListPrice: maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    hasSingleStory: singleStory,
    hasPool: mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
  });

  // Builds a payload from current filter state, with optional overrides for
  // values being changed in the same tick (which the async state setters
  // wouldn't reflect yet) and for the location.
  const buildSearchPayload = (
    override: Partial<PropertySearchQuery> = {},
  ): GetMarketplacePropertySearchQueryVariables =>
    buildPayloadFromQuery({ ...getCanonical(), ...override }, override);

  return {
    resetFilters,
    resetPage,
    applyCanonical,
    buildSearchPayload,
    getCanonical,
    get: {
      minBeds,
      minBaths,
      canSelfTour,
      minPrice,
      maxPrice,
      singleFamily,
      condo,
      townhome,
      noHoas,
      singleStory,
      mustHavePool,
      minYearBuilt,
      maxYearBuilt,
      minLotSize,
      maxLotSize,
      minSquareFootage,
      maxSquareFootage,
      pageNumber,
    },
    set: {
      setBedrooms,
      setBathrooms,
      setCanSelfTour,
      setMinPrice,
      setMaxPrice,
      setSingleFamily,
      setCondo,
      setTownhome,
      setNoHoas,
      setSingleStory,
      setMustHavePool,
      setMinYearBuilt,
      setMaxYearBuilt,
      setMinLotSize,
      setMaxLotSize,
      setMinSquareFootage,
      setMaxSquareFootage,
      setPageNumber,
    },
  };
};
